import { _ } from "../../../core/polyglot/polyglot.js";
import AsyncStateWidget from "../../../core/chrome/AsyncStateWidget.js";
import TextRepresentationAccountSpecificationVisitor from "../../../models/accountSpecification/visitors/TextRepresentationAccountSpecificationVisitor.js";
import updatingTitleWidget from "../updatingTitleWidget.js";

/**
 * `ForecastTemplateConfigurationWidget` is a widget to display and edit
 * the company's default ForecastTemplate configuration.
 *
 * The configuration is edited using the forecast onboarding wizard.
 *
 * @param {string} companyId - Unique identifier of the company owning
 * the default configuration.
 */
export default class ForecastTemplateConfigurationWidget extends AsyncStateWidget {
	constructor({ companyId }) {
		super(...arguments);

		this._configuration = {};
		this._companyId = companyId;

		this._updatingTitleWidget = updatingTitleWidget({
			title: () => {},
		});
	}

	setConfiguration(configuration) {
		this._configuration = configuration;

		if (configuration) {
			this._showContent();
		} else {
			this._showEmpty();
		}
	}

	_renderLoadedContentOn(html) {
		html.div(
			{ klass: "row" },
			this._renderConfigurationGeneralSettingsOn.bind(this),
			this._renderConfigurationVatSettingsOn.bind(this),
			this._renderConfigurationOtherSettingsOn.bind(this),
		);

		this._renderOpenWizardButtonOn(html);
	}

	_renderEmptyContentOn(html) {
		html.div(_("No default forecast template configuration."));
		this._renderOpenWizardButtonOn(html);
	}

	_renderOpenWizardButtonOn(html) {
		html.button(
			{
				class: "btn btn-primary",
				type: "button",
				"data-test": "run-setup-wizard",
				click: this._openWizard.bind(this),
			},
			_("Run setup wizard"),
		);
	}

	_renderConfigurationGeneralSettingsOn(html) {
		html.div(
			{ klass: "col-sm-12 col-md-4" },
			html.table(
				{ klass: "table table-auto" },
				this._fieldRenderer({
					label: _("Bank account"),
					value: this._configuration.getBankAccount(),
					dataTestName: "cash-account-value",
				}),
				this._fieldRenderer({
					label: _("Existing accounts payable group"),
					value: this._specificationRenderer(
						this._configuration.getExistingAccountsPayable(),
					),
					dataTestName: "accounts-payable-value",
				}),
				this._fieldRenderer({
					label: _("Existing accounts receivable group"),
					value: this._specificationRenderer(
						this._configuration.getExistingAccountsReceivable(),
					),
					dataTestName: "accounts-receivable-value",
				}),
				this._fieldRenderer({
					label: _("Existing input VAT group"),
					value: this._specificationRenderer(
						this._configuration.getIncomingVatGroup(),
					),
					dataTestName: "incoming-vat-group-value",
				}),
				this._fieldRenderer({
					label: _("Existing output VAT group"),
					value: this._specificationRenderer(
						this._configuration.getOutgoingVatGroup(),
					),
					dataTestName: "outgoing-vat-group-value",
				}),
				this._fieldRenderer({
					label: _("Existing net VAT accounts"),
					value: this._specificationRenderer(
						this._configuration.getSumVatGroup(),
					),
					dataTestName: "sum-vat-group-value",
				}),
				this._fieldRenderer({
					label: _("VAT payment"),
					value: _("{vatPaymentPeriod}, {vatPaymentLatency} days", {
						vatPaymentPeriod: this._configuration
							.getVatPaymentModel()
							.getVatPaymentPeriodName(),
						vatPaymentLatency: this._configuration
							.getVatPaymentModel()
							.getVatPaymentLatency(),
					}),
					dataTestName: "vat-payment-value",
				}),
			),
		);
	}

	_renderConfigurationOtherSettingsOn(html) {
		html.div(
			{ klass: "col-sm-12 col-md-4" },
			html.table(
				{ klass: "table table-auto" },
				this._fieldRenderer({
					label: _("Reference periods"),
					value: this._configuration.getNumberOfReferencePeriods(),
					dataTestName: "reference-periods-value",
				}),
				this._fieldRenderer({
					label: _("Predicted periods"),
					value: this._configuration.getNumberOfPredictedPeriods(),
					dataTestName: "predicted-periods-value",
				}),
				this._fieldRenderer({
					label: _("Existing accounts payable delays"),
					value: this._configuration
						.getExistingAccountsPayableDelays()
						.map((delay) => ` (${delay}%)`)
						.toString(),
					dataTestName: "accounts-payable-delay-value",
				}),
				this._fieldRenderer({
					label: _("Existing accounts receivable delays"),
					value: this._configuration
						.getExistingAccountsReceivableDelays()
						.map((delay) => ` (${delay}%)`)
						.toString(),
					dataTestName: "accounts-receivable-delay-value",
				}),
				this._fieldRenderer({
					label: _("Forecasted accounts payable delays"),
					value: this._configuration
						.getForecastAccountsPayableDelays()
						.map((delay) => ` (${delay}%)`)
						.toString(),
					dataTestName: "account-payable-delay-value",
				}),
				this._fieldRenderer({
					label: _("Forecasted accounts receivable delays"),
					value: this._configuration
						.getForecastAccountsReceivableDelays()
						.map((delay) => ` (${delay}%)`)
						.toString(),
					dataTestName: "account-receivable-delay-value",
				}),
			),
		);
	}

	_renderConfigurationVatSettingsOn(html) {
		html.div(
			{ klass: "col-sm-12 col-md-4" },
			html.table(
				{ klass: "table table-auto" },
				this._fieldRenderer({
					label: _("Main input VAT account"),
					value: this._configuration.getIncomingVat(),
					dataTestName: "incoming-vat-account-value",
				}),
				this._fieldRenderer({
					label: _("Main output VAT account"),
					value: this._configuration.getOutgoingVat(),
					dataTestName: "outgoing-vat-account-value",
				}),
				this._fieldRenderer({
					label: _("Main net VAT account"),
					value: this._configuration.getVatSumAccount(),
					dataTestName: "accounts-receivable-value",
				}),
				this._fieldRenderer({
					label: _("Forecasted accounts payable"),
					value: this._specificationRenderer(
						this._configuration.getForecastAccountsPayable(),
					),
					dataTestName: "account-payable-value",
				}),
				this._fieldRenderer({
					label: _("Forecasted accounts receivable"),
					value: this._specificationRenderer(
						this._configuration.getForecastAccountsReceivable(),
					),
					dataTestName: "account-receivable-value",
				}),
			),
		);
	}

	_fieldRenderer({ label, value, dataTestName }) {
		return (html) =>
			html.tr(
				html.th({ klass: "text-right" }, label),
				html.td({ "data-test": dataTestName }, value || _("N/A")),
			);
	}

	_specificationRenderer(specification) {
		if (!specification) return (_html) => {};

		return new TextRepresentationAccountSpecificationVisitor().visit(
			specification,
		);
	}

	_openWizard() {
		this._redirectToLocationPath(
			`/Accounting2/Company/${this._companyId}#!/forecast-company-settings`,
		);
	}
}
